import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { AxiosError } from 'axios'
import type { Stage } from '@/config/stages'
import { DEFAULT_STAGES } from '@/config/stages'
import { kanbanApi } from '@/api/chatwoot'

// Envelope retornado pelo backend (ver kanban-api/src/types.ts)
interface ConfigEnvelope {
  config: {
    stages: Stage[]
    supervisorUserIds: number[]
  }
  updatedAt: string
  updatedByUserId: number
  updatedByUserName: string
  schemaVersion: number
}

export interface SaveResult {
  ok: boolean
  error?: string
}

export const useKanbanConfigStore = defineStore('kanban-config', () => {
  const stages = ref<Stage[]>([...DEFAULT_STAGES])
  const supervisorUserIds = ref<number[]>([])
  const loading = ref(false)
  const saving = ref(false)
  const error = ref<string | null>(null)
  const isFromBackend = ref(false)
  const updatedAt = ref<string | null>(null)
  const updatedByUserName = ref<string | null>(null)

  const stageKeys = computed(() => stages.value.map((s) => s.key))

  function findStage(key: string): Stage | undefined {
    return stages.value.find((s) => s.key === key)
  }

  function applyEnvelope(env: ConfigEnvelope) {
    stages.value = env.config.stages
    supervisorUserIds.value = env.config.supervisorUserIds
    updatedAt.value = env.updatedAt
    updatedByUserName.value = env.updatedByUserName
    isFromBackend.value = true
  }

  async function load(): Promise<void> {
    if (loading.value) return
    loading.value = true
    error.value = null
    try {
      const { data } = await kanbanApi.get<ConfigEnvelope>('/config')
      applyEnvelope(data)
    } catch (e) {
      const axiosError = e as AxiosError<{ error?: string }>
      const msg = axiosError.response?.data?.error ?? axiosError.message
      // Não vira banner — apenas log; fallback aos defaults preserva UX
      console.warn(
        '[kanban-config] falha ao carregar do backend; usando defaults:',
        msg,
      )
      error.value = msg
      isFromBackend.value = false
    } finally {
      loading.value = false
    }
  }

  async function save(newConfig: {
    stages: Stage[]
    supervisorUserIds: number[]
  }): Promise<SaveResult> {
    if (saving.value) return { ok: false, error: 'salvando já em andamento' }
    saving.value = true
    error.value = null
    try {
      const { data } = await kanbanApi.put<ConfigEnvelope>(
        '/config',
        newConfig,
      )
      applyEnvelope(data)
      return { ok: true }
    } catch (e) {
      const axiosError = e as AxiosError<{ error?: string }>
      const msg =
        axiosError.response?.data?.error ??
        axiosError.message ??
        'Falha ao salvar config'
      error.value = msg
      return { ok: false, error: msg }
    } finally {
      saving.value = false
    }
  }

  return {
    stages,
    stageKeys,
    supervisorUserIds,
    loading,
    saving,
    error,
    isFromBackend,
    updatedAt,
    updatedByUserName,
    findStage,
    load,
    save,
  }
})
